import {
  Flex,
  HStack,
  Text,
  Image,
  Button,
  Box,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  VStack,
  useDisclosure,
  useBreakpointValue,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  useToast,
} from '@chakra-ui/react';
import { FaChartLine } from 'react-icons/fa6';
import { MdOutlineSmartToy } from 'react-icons/md';
import { AiOutlineMessage } from 'react-icons/ai';
import { HiOutlineOfficeBuilding, HiMenu } from 'react-icons/hi';
import { IoPricetagOutline, IoHelpCircleOutline } from 'react-icons/io5';
import { useLocation, useNavigate } from 'react-router-dom';
import BetaTag from '../helivan/BetaTag';
import { HelivanColor } from '../helivan/HelivanColor';
import { ProfileDropdown } from './ProfileDropdown';
import { StyledMenuSelect } from '../helivan/StyledSelect';
import { useState, type MouseEvent } from 'react';
import { useProfileDropdownQuery, useSetUserOrganizationMutation, useCreateOrganizationWithOwnerMutation } from '../../generated/graphql';
import { useTour } from '../Tour/TourContext';
import { useAuth } from '../../contexts/AuthContext';

const NavTab = ({
  label,
  icon: Icon,
  isActive,
  onClick,
}: {
  label: string;
  icon: React.ElementType;
  isActive: boolean;
  onClick: () => void;
}) => (
  <Flex
    alignItems="center"
    gap={2}
    px={5}
    py={2}
    cursor="pointer"
    borderRadius="full"
    bg={isActive ? 'white' : 'transparent'}
    border="1px solid"
    borderColor={isActive ? HelivanColor.text.helivanBlue : HelivanColor.border.primary}
    boxShadow={isActive ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'}
    color={isActive ? HelivanColor.text.helivanBlue : HelivanColor.text.blueMid}
    fontWeight={isActive ? 600 : 500}
    transition="all 0.2s"
    _hover={{
      bg: 'white',
      borderColor: HelivanColor.text.helivanBlue,
      color: HelivanColor.text.helivanBlue,
    }}
    onClick={onClick}
  >
    <Icon size={16} />
    <Text fontSize="14px">{label}</Text>
  </Flex>
);

// Mobile navigation item
const MobileNavItem = ({
  label,
  icon: Icon,
  isActive,
  onClick,
}: {
  label: string;
  icon: React.ElementType;
  isActive: boolean;
  onClick: () => void;
}) => (
  <Flex
    alignItems="center"
    gap={3}
    px={4}
    py={3}
    cursor="pointer"
    borderRadius="12px"
    bg={isActive ? HelivanColor.background.lightestGrey : 'transparent'}
    color={isActive ? HelivanColor.text.helivanBlue : HelivanColor.text.blueDark}
    fontWeight={isActive ? 600 : 400}
    transition="all 0.2s"
    _hover={{
      bg: HelivanColor.background.lightestGrey,
    }}
    onClick={onClick}
    width="100%"
  >
    <Icon size={20} />
    <Text fontSize="16px">{label}</Text>
  </Flex>
);

export const GlobalTopbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: profileData, loading: profileLoading } = useProfileDropdownQuery();
  const [switchOrg] = useSetUserOrganizationMutation();
  const { start: startMainTour, startWatchTour, startDemoTour } = useTour();
  const { isAuthenticated } = useAuth();

  // The "?" replays whichever tour fits the current page: the demo tour on the
  // sample watch, the watch-detail tour on a real watch, otherwise the main tour.
  const startTour = () => {
    const path = location.pathname;
    if (path === '/projects/demo') startDemoTour();
    else if (path.startsWith('/projects/')) startWatchTour();
    else startMainTour();
  };
  const allOrgs = profileData?.user?.organizations ?? [];
  const currentOrg = profileData?.user?.currentOrganization;
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Responsive breakpoint - show hamburger menu on mobile
  const isMobile = useBreakpointValue({ base: true, md: false });

  const showNavTabs =
    location.pathname === '/projects' ||
    location.pathname.startsWith('/projects/') ||
    location.pathname === '/personas' ||
    location.pathname.startsWith('/personas/') ||
    location.pathname === '/agents' ||
    location.pathname.startsWith('/agents/') ||
    location.pathname === '/home' ||
    location.pathname === '/settings' ||
    location.pathname === '/pricing';

  const handleOrgSwitch = (orgId: string) => {
    if (orgId === currentOrg?.id) return;
    switchOrg({
      variables: { orgId },
      onCompleted: () => {
        window.location.reload();
      },
    });
  };

  // Create organization (creates it, makes you owner, switches you to it).
  const createOrgModal = useDisclosure();
  const [newOrgName, setNewOrgName] = useState('');
  const [createOrg, { loading: creatingOrg }] = useCreateOrganizationWithOwnerMutation();
  const toast = useToast();

  const handleCreateOrg = async () => {
    const name = newOrgName.trim();
    if (!name) return;
    try {
      await createOrg({ variables: { name } });
      // The mutation sets it as the current org; reload to pick up the new tenant
      // context everywhere (mirrors handleOrgSwitch).
      window.location.reload();
    } catch (e) {
      toast({
        title: 'Could not create organization',
        description: e instanceof Error ? e.message : 'Please try again.',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const createOrgFooter = (
    <Box
      as="button"
      width="100%"
      textAlign="left"
      px="14px"
      py="10px"
      mt="4px"
      borderTop="1px solid #e2e8f0"
      fontSize="14px"
      fontWeight={600}
      color={HelivanColor.text.helivanBlue}
      display="flex"
      alignItems="center"
      gap="8px"
      // onMouseDown (not onClick) so it fires before react-select closes the menu.
      onMouseDown={(e: MouseEvent) => {
        e.preventDefault();
        setNewOrgName('');
        createOrgModal.onOpen();
      }}
      _hover={{ bg: '#f8fafc' }}
    >
      <HiOutlineOfficeBuilding size={14} /> Create organization
    </Box>
  );

  const handleMobileNav = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <Flex
      direction="column"
      bg={HelivanColor.background.secondary}
    >
      {/* Main header row */}
      <Flex
        alignItems="center"
        px={{ base: 4, md: 6 }}
        py={3}
        justifyContent="space-between"
      >
        {/* Left: Hamburger (mobile) + Logo */}
        <HStack spacing={{ base: 2, md: 3 }} minWidth={{ base: 'auto', md: '200px' }}>
          {/* Hamburger menu button - mobile only */}
          {isMobile && showNavTabs && (
            <IconButton
              aria-label="Open menu"
              icon={<HiMenu size={24} />}
              variant="ghost"
              color={HelivanColor.text.blueDark}
              onClick={onOpen}
              _hover={{ bg: 'rgba(255,255,255,0.5)' }}
            />
          )}
          <HStack spacing={2} onClick={() => navigate('/projects')} cursor="pointer">
            <Image
              src="/img/agent-watch-icon.svg"
              alt="AgentWatch Logo"
              height={{ base: '28px', md: '32px' }}
              width={{ base: '28px', md: '32px' }}
              objectFit="contain"
            />
            <Text
              fontSize={{ base: '18px', md: '20px' }}
              fontWeight={600}
              color={HelivanColor.text.blueDark}
              display={{ base: 'none', sm: 'block' }}
            >
              AgentWatch
            </Text>
          </HStack>
          <Box display={{ base: 'none', lg: 'block' }}>
            <BetaTag />
          </Box>
        </HStack>

        {/* Center: Navigation tabs - desktop only */}
        {showNavTabs && !isMobile && (
          <Flex
            data-tour="nav-tabs"
            alignItems="center"
            gap={2}
            bg={HelivanColor.background.lightestGrey}
            borderRadius="full"
            p={1}
          >
            <NavTab
              label="Agents"
              icon={MdOutlineSmartToy}
              isActive={location.pathname === '/personas' || location.pathname.startsWith('/agents')}
              onClick={() => navigate('/agents')}
            />
            <NavTab
              label="Watches"
              icon={FaChartLine}
              isActive={location.pathname === '/projects' || location.pathname.startsWith('/projects/')}
              onClick={() => navigate('/projects')}
            />
          </Flex>
        )}

        {/* Right: Pricing + Organization switcher + Profile */}
        <HStack spacing={{ base: 1, md: 3 }} minWidth={{ base: 'auto', md: '200px' }} justifyContent="flex-end">
          {/* Pricing - hidden on mobile */}
          <Button
            variant="ghost"
            size="sm"
            fontWeight={400}
            color={HelivanColor.text.blueDark}
            _hover={{ bg: 'rgba(255,255,255,0.5)' }}
            onClick={() => navigate('/pricing')}
            display={{ base: 'none', md: 'flex' }}
          >
            <HStack spacing={2}>
              <IoPricetagOutline size={16} />
              <Text fontSize="13px">Pricing</Text>
            </HStack>
          </Button>
          {/* Take a tour - desktop only */}
          {showNavTabs && !isMobile && (
            <IconButton
              data-tour="help-button"
              aria-label="Take a tour"
              icon={<IoHelpCircleOutline size={20} />}
              variant="ghost"
              size="sm"
              color={HelivanColor.text.blueDark}
              _hover={{ bg: 'rgba(255,255,255,0.5)' }}
              onClick={() => startTour()}
            />
          )}
          {/* Org switcher - hidden on mobile, fixed width to prevent layout shift */}
          {!isMobile && (
            <Box minWidth="160px">
              {!isAuthenticated ? (
                <HStack
                  as="button"
                  spacing={2}
                  px={3}
                  py={1}
                  color={HelivanColor.text.blueDark}
                  onClick={() => navigate('/login')}
                  _hover={{ textDecoration: 'underline' }}
                >
                  <HiOutlineOfficeBuilding size={16} />
                  <Text fontSize="13px">Public</Text>
                </HStack>
              ) : !profileLoading && allOrgs.length >= 1 ? (
                <StyledMenuSelect
                  options={allOrgs.map((org) => ({
                    value: org.id,
                    label: org.name,
                  }))}
                  value={currentOrg?.id || null}
                  onChange={handleOrgSwitch}
                  icon={<HiOutlineOfficeBuilding size={16} color={HelivanColor.text.blueDark} />}
                  maxWidth="120px"
                  footer={createOrgFooter}
                />
              ) : null}
            </Box>
          )}
          <ProfileDropdown />
        </HStack>
      </Flex>

      {/* Mobile Navigation Drawer */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent maxW="280px">
          <DrawerCloseButton />
          <DrawerBody pt={12}>
            <VStack spacing={2} align="stretch">
              {/* Navigation items */}
              <MobileNavItem
                label="Agents"
                icon={MdOutlineSmartToy}
                isActive={location.pathname === '/personas' || location.pathname.startsWith('/agents')}
                onClick={() => handleMobileNav('/agents')}
              />
              <MobileNavItem
                label="Watches"
                icon={FaChartLine}
                isActive={location.pathname === '/projects' || location.pathname.startsWith('/projects/')}
                onClick={() => handleMobileNav('/projects')}
              />

              <Divider my={2} />

              <MobileNavItem
                label="Pricing"
                icon={IoPricetagOutline}
                isActive={location.pathname === '/pricing'}
                onClick={() => handleMobileNav('/pricing')}
              />

              <MobileNavItem
                label="Take a tour"
                icon={IoHelpCircleOutline}
                isActive={false}
                onClick={() => {
                  onClose();
                  startTour();
                }}
              />

              {/* Organization switcher on mobile */}
              {allOrgs.length >= 1 && (
                <>
                  <Divider my={2} />
                  <Text fontSize="12px" fontWeight={600} color={HelivanColor.text.blueMid} px={4} pt={2}>
                    Organization
                  </Text>
                  {allOrgs.map((org) => (
                    <Flex
                      key={org.id}
                      alignItems="center"
                      gap={3}
                      px={4}
                      py={3}
                      cursor="pointer"
                      borderRadius="12px"
                      bg={org.id === currentOrg?.id ? HelivanColor.background.lightestGrey : 'transparent'}
                      color={org.id === currentOrg?.id ? HelivanColor.text.helivanBlue : HelivanColor.text.blueDark}
                      fontWeight={org.id === currentOrg?.id ? 600 : 400}
                      onClick={() => {
                        handleOrgSwitch(org.id);
                        onClose();
                      }}
                    >
                      <HiOutlineOfficeBuilding size={20} />
                      <Text fontSize="16px">{org.name}</Text>
                    </Flex>
                  ))}
                  <Flex
                    alignItems="center"
                    gap={3}
                    px={4}
                    py={3}
                    cursor="pointer"
                    borderRadius="12px"
                    color={HelivanColor.text.helivanBlue}
                    fontWeight={600}
                    onClick={() => {
                      onClose();
                      setNewOrgName('');
                      createOrgModal.onOpen();
                    }}
                  >
                    <HiOutlineOfficeBuilding size={20} />
                    <Text fontSize="16px">Create organization</Text>
                  </Flex>
                </>
              )}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Create organization modal */}
      <Modal isOpen={createOrgModal.isOpen} onClose={createOrgModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create organization</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input
              placeholder="Organization name"
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newOrgName.trim() && !creatingOrg) handleCreateOrg();
              }}
              autoFocus
            />
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={createOrgModal.onClose}>
              Cancel
            </Button>
            <Button
              bg={HelivanColor.text.helivanBlue}
              color="white"
              _hover={{ bg: HelivanColor.text.helivanBlue }}
              isLoading={creatingOrg}
              isDisabled={!newOrgName.trim()}
              onClick={handleCreateOrg}
            >
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};
